/* eslint-disable no-param-reassign */
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum, RumEvent } from '@datadog/browser-rum';

import { getAppPlatform, readNativeShellInfo } from 'src/native/appInfo';

// Session Replay is no longer recorded for every session. It is started manually, and only
// once a session shows real trouble — see trackReplayTriggers below. Recording cannot be
// backfilled, so a triggered replay covers the session from the trigger point onward.
const ReplayErrorThreshold = 2;

const ProductionEnv = 'production';
const NonProductionSessionSampleRate = 10;

const isProduction = import.meta.env.VITE_ENV === ProductionEnv;

let currentSessionId: string | undefined;
let sessionErrorCount = 0;
let replayRequested = false;

const requestSessionReplay = () => {
    if (replayRequested) {
        return;
    }

    replayRequested = true;

    // Deferred so we never re-enter the SDK from inside beforeSend.
    setTimeout(() => datadogRum.startSessionReplayRecording(), 0);
};

// beforeSend sees every event, which makes it the cheapest place to watch a session's error
// and frustration signals. Counters are per session, so reset them when the id rolls over.
const trackReplayTriggers = (event: RumEvent) => {
    if (event.session.id !== currentSessionId) {
        currentSessionId = event.session.id;
        sessionErrorCount = 0;
        replayRequested = false;
    }

    if (event.type === 'error') {
        sessionErrorCount += 1;

        if (sessionErrorCount >= ReplayErrorThreshold) {
            requestSessionReplay();
        }
    }

    if (event.type === 'action' && event.action?.frustration?.type.length) {
        requestSessionReplay();
    }
};

// Reference: https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#modify-the-content-of-a-rum-event
const beforeSend = (event: RumEvent) => {
    // Watch for replay triggers before the filtering below can discard the event.
    trackReplayTriggers(event);

    // If is a RumActionEvent
    if (event.type === 'action') {
        // If the action name is 'discard action', discard the event
        const actionName = event.action?.target?.name || '';
        if (actionName === 'discard action') {
            return false;
        }

        // Frustrated actions are kept whatever their name length — they are the signal the
        // replay triggers depend on, and the length rule only exists to trim noisy auto-names.
        const isFrustrated = Boolean(event.action?.frustration?.type.length);

        // If the action name is too long, discard the event
        if (!isFrustrated && actionName.length > 30) {
            return false;
        }
    }

    // If is a RumViewEvent
    if (event.type === 'view') {
        if (event.view?.url) {
            // Redace tokens from event.view.url
            event.view.url = event.view.url.replace(/access_token=[^&]*/, 'access_token=REDACTED');
            event.view.url = event.view.url.replace(/id_token=[^&]*/, 'id_token=REDACTED');
        }

        if (event.view?.referrer) {
            // Redact tokens from event.view.referrer
            event.view.referrer = event.view.referrer.replace(
                /access_token=[^&]*/,
                'access_token=REDACTED'
            );
            event.view.referrer = event.view.referrer.replace(
                /id_token=[^&]*/,
                'id_token=REDACTED'
            );
        }
    }

    // If is a RumErrorEvent
    if (event.type === 'error') {
        // Redact tokens from event.error.resource.url
        if (event.error?.resource) {
            event.error.resource.url = event.error.resource.url.replace(
                /access_token=[^&]*/,
                'access_token=REDACTED'
            );
            event.error.resource.url = event.error.resource.url.replace(
                /id_token=[^&]*/,
                'id_token=REDACTED'
            );
        }
    }

    return true;
};

// Reference: https://docs.datadoghq.com/real_user_monitoring/browser/#npm
if (import.meta.env.PROD) {
    datadogRum.init({
        applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
        clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
        site: import.meta.env.VITE_DATADOG_SITE,
        service: import.meta.env.VITE_DATADOG_SERVICE,
        env: import.meta.env.VITE_ENV,
        version: import.meta.env.VITE_VERSION,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        beforeSend,
        allowedTracingUrls: [
            import.meta.env.VITE_SAGE_ADMIN_BASE_URL,
            import.meta.env.VITE_SAGE_INSIGHTS_ENGINE_BASE_URL,
            import.meta.env.VITE_SAGE_MESSAGE_ORCHESTRATION_BASE_URL,
            import.meta.env.VITE_SAGE_STAFF_COMMS_SERVICE_BASE_URL,
            import.meta.env.VITE_SAGE_TABULAR_RECORD_SERVICE_BASE_URL,
        ],

        // Session Replay Properties
        sessionSampleRate: isProduction ? 100 : NonProductionSessionSampleRate,
        sessionReplaySampleRate: 100, // eligibility only — recording is started manually
        startSessionReplayRecordingManually: true,
        defaultPrivacyLevel: 'mask-user-input',
    });

    // Which shell the session is running in. Set here rather than in AnalyticsContext so the events
    // RUM collects before React mounts carry the dimension too.
    datadogRum.setGlobalContextProperty('platform', getAppPlatform());

    // Needs a bridge round-trip, so the earliest events in a native session go without it.
    readNativeShellInfo().then((info) => {
        if (info) {
            datadogRum.setGlobalContextProperty('shell', info);
        }
    });

    datadogLogs.init({
        clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
        site: import.meta.env.VITE_DATADOG_SITE,
        service: import.meta.env.VITE_DATADOG_SERVICE,
        env: import.meta.env.VITE_ENV,
        version: import.meta.env.VITE_VERSION,
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
    });
}

export default datadogRum;
